import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpRight, Package } from 'lucide-react';
import type { MarketTrendSeries, MarketTrendFreshnessBucket } from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { formatUsd } from '../lib/format';
import { FinancialsReportScreen } from '../components/financials/FinancialsReportScreen';
import { CalculationBasisTrigger } from '../components/financials/CalculationBasisDrawer';
import { MarketTrendLineChart } from '../components/charts/MarketTrendLineChart';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';

/**
 * /market-trends — SIL-304 (W4, financials-hifi-flow-spec.md slice 6).
 * Fleet-scoped, read-only composition over `GET /api/client/market-trends`
 * (SIL-295): for each owned asset class / top SKU, a 12-18mo price trend,
 * current p25/median/p75 band, comps count + bucketed freshness, and delta
 * vs the org's own book value per unit.
 *
 * The class/SKU picker is built ENTIRELY from the response's own `series`
 * array — never a separate reference-data call — so it is structurally
 * impossible to offer an un-owned class/SKU (the server already resolved
 * the owned set; this page just groups and filters what it returned).
 *
 * Below the k-anonymity floor the server nulls the band + count and sets
 * `current.insufficientDepth`; this page renders an explicit "insufficient
 * market depth" state for that card — never an empty chart, never a median
 * computed from a suppressed field (spec: "never false-precision").
 */
export const Route = createFileRoute('/market-trends')({
  component: MarketTrendsPage,
  // SIL-566 (I2-B4) — cross-pillar deep link from Financial Position's
  // by-asset-class table: ?classId=<owned asset class> preselects this
  // page's own class filter on load. `skuId` is accepted for symmetry (a
  // future deep-linker landing on a specific SKU card) — nothing on this
  // page currently sends one, but honoring it costs nothing extra since the
  // filter state already accepts a SKU value.
  validateSearch: (search: Record<string, unknown>): { classId?: string; skuId?: string } => ({
    classId: typeof search.classId === 'string' && search.classId ? search.classId : undefined,
    skuId: typeof search.skuId === 'string' && search.skuId ? search.skuId : undefined,
  }),
});

const ALL = 'all' as const;

function MarketTrendsPage() {
  const { classId, skuId } = Route.useSearch();
  const trendsQ = useQuery({
    queryKey: qk.marketTrends(),
    queryFn: async () => {
      const res = await api.api.client['market-trends'].$get({ query: {} });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return res.json();
    },
  });

  const series = trendsQ.data?.series ?? [];

  // SIL-566: seeded from the deep link's search params (lazy — the initial
  // value only, same "read once" contract as the scenario wizard's classId
  // preselect above).
  const [classFilter, setClassFilter] = useState<string>(() => classId ?? ALL);
  const [skuFilter, setSkuFilter] = useState<string>(() => skuId ?? ALL);

  // Options are derived ONLY from the response — the server already scoped
  // `series` to the caller org's owned assets, so there is no way for this
  // picker to surface a class/SKU the org doesn't own.
  const classOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const s of series) byId.set(s.assetClassId, s.assetClassName);
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [series]);

  const seriesInClass = useMemo(
    () => (classFilter === ALL ? series : series.filter((s) => s.assetClassId === classFilter)),
    [series, classFilter],
  );

  const skuOptions = useMemo(
    () =>
      [...seriesInClass]
        .sort((a, b) => b.ownedAssetCount - a.ownedAssetCount)
        .map((s) => ({ id: s.skuId, label: s.label })),
    [seriesInClass],
  );

  const cards = useMemo(() => {
    if (skuFilter !== ALL) {
      const one = series.find((s) => s.skuId === skuFilter);
      return one ? [one] : [];
    }
    if (classFilter !== ALL) {
      // A class is selected but no specific SKU — show every owned SKU in
      // that class, top-owned first.
      return [...seriesInClass].sort((a, b) => b.ownedAssetCount - a.ownedAssetCount);
    }
    // Default view: one card per owned asset class, the class's top SKU by
    // owned-asset count (spec slice 6: "per owned asset class / top SKU").
    const topPerClass = new Map<string, MarketTrendSeries>();
    for (const s of series) {
      const cur = topPerClass.get(s.assetClassId);
      if (!cur || s.ownedAssetCount > cur.ownedAssetCount) topPerClass.set(s.assetClassId, s);
    }
    return [...topPerClass.values()].sort((a, b) => a.assetClassName.localeCompare(b.assetClassName));
  }, [series, seriesInClass, classFilter, skuFilter]);

  return (
    <FinancialsReportScreen
      title="Market Outlook"
      pinTo="/market-trends"
      subtitle="Fleet-scoped market pricing for your owned asset classes and SKUs — 12-18mo trend, current band, and delta vs your own book value."
      query={trendsQ}
      empty={(data) =>
        data.series.length === 0
          ? {
              icon: Package,
              title: 'No market trend data yet',
              body: 'Once your fleet has assets with matched SKUs, comp-derived trends will appear here.',
            }
          : null
      }
    >
      {() => (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Asset class
              <select
                value={classFilter}
                onChange={(e) => {
                  setClassFilter(e.target.value);
                  setSkuFilter(ALL);
                }}
                className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground"
              >
                <option value={ALL}>All owned classes</option>
                {classOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              SKU
              <select
                value={skuFilter}
                onChange={(e) => setSkuFilter(e.target.value)}
                disabled={classFilter === ALL}
                className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value={ALL}>
                  {classFilter === ALL ? 'Top SKU per class' : 'All SKUs in class'}
                </option>
                {skuOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {cards.map((s) => (
              <MarketTrendCard key={s.skuId} series={s} />
            ))}
          </div>
        </>
      )}
    </FinancialsReportScreen>
  );
}

// ---------------------------------------------------------------------------
// Per-SKU card
// ---------------------------------------------------------------------------

const FRESHNESS_LABEL: Record<MarketTrendFreshnessBucket, string> = {
  within_30_days: 'Within 30 days',
  within_60_days: 'Within 60 days',
  within_90_days: 'Within 90 days',
  over_90_days: 'Over 90 days',
  unknown: 'Unknown',
};

const FRESHNESS_TONE: Record<MarketTrendFreshnessBucket, string> = {
  within_30_days: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  within_60_days: 'bg-lime-50 text-lime-700 ring-lime-200',
  within_90_days: 'bg-amber-50 text-amber-700 ring-amber-200',
  over_90_days: 'bg-red-50 text-red-700 ring-red-200',
  unknown: 'bg-muted text-muted-foreground ring-border',
};

function MarketTrendCard({ series }: { series: MarketTrendSeries }) {
  const { current } = series;

  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-2">
        <div>
          <SectionLabel className="gap-1.5 inline-flex items-center">
            {series.assetClassName}
          </SectionLabel>
          <h2 className="mt-0.5 flex items-center gap-1.5 text-[15px] font-semibold text-foreground">
            {series.label}
            <CalculationBasisTrigger title={`${series.label} — market trend`} basis={series.basis} />
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {series.ownedAssetCount.toLocaleString()} owned {series.ownedAssetCount === 1 ? 'unit' : 'units'}
          </p>
        </div>
        <FreshnessBadge bucket={current.freshnessBucket} />
      </div>

      <div className="mt-3">
        {current.insufficientDepth ? (
          <InsufficientDepthPanel />
        ) : (
          <MarketTrendLineChart trend={series.trend} />
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border pt-3 text-center">
        <BandStat label="P25" value={current.p25Usd} suppressed={current.insufficientDepth} />
        <BandStat label="Median" value={current.medianUsd} suppressed={current.insufficientDepth} emphasize />
        <BandStat label="P75" value={current.p75Usd} suppressed={current.insufficientDepth} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-xs">
        <span className="text-muted-foreground">
          {current.insufficientDepth || current.compsCount === null
            ? 'Comps: insufficient depth'
            : `${current.compsCount.toLocaleString()} comps`}
        </span>
        <DeltaVsBook value={series.deltaVsBookValueUsd} />
      </div>

      {/* SIL-566 (I2-B4): cross-pillar deep link back to this class's row in
          Financial Position's by-asset-class table. */}
      <Link
        to="/financials/book-vs-fmv"
        search={{ classId: series.assetClassId }}
        className="mt-3 inline-flex items-center gap-1 border-t border-border pt-3 text-xs font-medium text-brand-600 hover:underline"
      >
        View in Financial Position <ArrowUpRight className="size-3.5" />
      </Link>
    </Card>
  );
}

function FreshnessBadge({ bucket }: { bucket: MarketTrendFreshnessBucket }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        FRESHNESS_TONE[bucket],
      )}
    >
      {FRESHNESS_LABEL[bucket]}
    </span>
  );
}

function BandStat({
  label,
  value,
  suppressed,
  emphasize,
}: {
  label: string;
  value: number | null;
  suppressed?: boolean;
  emphasize?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-0.5 tabular-nums',
          emphasize ? 'text-base font-semibold' : 'text-sm font-medium',
          suppressed ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {suppressed || value === null ? '—' : formatUsd(value, { compact: true })}
      </p>
    </div>
  );
}

function DeltaVsBook({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="text-muted-foreground">Delta vs book: —</span>;
  }
  const positive = value >= 0;
  return (
    <span className={cn('font-medium tabular-nums', positive ? 'text-emerald-600' : 'text-red-500')}>
      {positive ? '+' : '−'}
      {formatUsd(Math.abs(value), { compact: true })} vs book/unit
    </span>
  );
}

/**
 * Below-k-anonymity-floor state (spec slice 6: "never an empty chart or
 * false-precision median"). Replaces the chart area entirely — never renders
 * an empty/zero-line chart, and the band stats below independently render
 * "—" for every numeric field per `suppressed` on `BandStat`.
 */
function InsufficientDepthPanel() {
  return (
    <div className="flex h-[180px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-amber-200 bg-amber-50/60 px-4 text-center">
      <AlertTriangle className="size-5 text-amber-500" />
      <p className="text-sm font-medium text-amber-800">Insufficient market depth</p>
      <p className="max-w-xs text-xs text-amber-700">
        Fewer than the anonymity floor of comparable sales are available for this SKU right now —
        the market band is suppressed rather than shown at false precision.
      </p>
    </div>
  );
}
